import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, User, Mail, Lock, BadgeCheck, ToggleLeft, ToggleRight, UserPlus, Edit
} from 'lucide-react';
import { authservice } from '../../../../components/api/authservice';

interface Props {
  onClose: () => void;
  onSubmit: () => void;
  userId?: string;
}

const tenantId = '6a6eee6f-75d7-4479-892d-80b5f225e004';

const AddUserModal: React.FC<Props> = ({ onClose, onSubmit, userId }) => {
  const isEditMode = !!userId;
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    roleID: '',
    isActive: true,
    tenantId: '',
  });
  const [error, setError] = useState('');
  const [roles, setRoles] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    if (isEditMode) {
      authservice.getSingleUser(tenantId, userId!).then(res => {
        const { user } = res;
        setForm({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          password: '',
          roleID: user.roleID || '',
          isActive: user.isActive,
          tenantId: user.tenantId || '',
        });
      }).catch(console.error);
    }
  }, [userId]);

  useEffect(() => {
    authservice.getRoles()
      .then(res => {
        const parsed = (res || []).map((r: any) => ({
          id: r.roleId,
          name: r.roleName || 'Unnamed Role',
        }));
        setRoles(parsed);
      })
      .catch(err => console.error('Failed to fetch roles', err));
  }, []);

  const handleChange = (field: string, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!form.firstName || !form.lastName || !form.email || !form.roleID) {
      return 'All fields marked with * are required.';
    }
    if (!isEditMode && form.password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }
    return '';
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    try {
      if (isEditMode) {
        await authservice.editUser({
          id: userId,
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          tenantId: form.tenantId,
          roleID: form.roleID,
          isactive: form.isActive,
        });
      } else {
        await authservice.registerUser({
          email: form.email,
          password: form.password,
          firstName: form.firstName,
          lastName: form.lastName,
          tenant_id: form.tenantId,
          isactive: form.isActive,
          createdBy: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
          roleID: form.roleID,
        });
      }
      onSubmit();
    } catch (err) {
      setError('Failed to process request. Please try again.');
      console.error(err);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-neutral-900 text-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-neutral-800 transition-all duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-900/30 flex items-center justify-center">
              {isEditMode ? <Edit className="w-5 h-5 text-indigo-400" /> : <UserPlus className="w-5 h-5 text-indigo-400" />}
            </div>
            <h3 className="text-lg font-bold">{isEditMode ? 'Edit User Profile' : 'Register New User'}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-neutral-800 transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">First Name *</label>
              <div className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 transition-all">
                <User className="w-4 h-4 text-gray-400" />
                <input type="text" value={form.firstName} onChange={(e) => handleChange('firstName', e.target.value)}
                  className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-gray-400"
                  placeholder="e.g. John" />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Last Name *</label>
              <div className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 transition-all">
                <User className="w-4 h-4 text-gray-400" />
                <input type="text" value={form.lastName} onChange={(e) => handleChange('lastName', e.target.value)}
                  className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-gray-400"
                  placeholder="e.g. Doe" />
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Email Address *</label>
            <div className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 transition-all">
              <Mail className="w-4 h-4 text-gray-400" />
              <input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)}
                className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-gray-400"
                placeholder="john.doe@company.com" />
            </div>
          </div>

          {!isEditMode && (
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Security Password *</label>
              <div className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 transition-all">
                <Lock className="w-4 h-4 text-gray-400" />
                <input type="password" value={form.password} onChange={(e) => handleChange('password', e.target.value)}
                  className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-gray-400"
                  placeholder="Min. 8 characters" />
              </div>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Assigned Role *</label>
            <div className="flex items-center bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 transition-all">
              <BadgeCheck className="w-4 h-4 text-gray-400 mr-2" />
              <select value={form.roleID} onChange={(e) => handleChange('roleID', e.target.value)}
                className="bg-transparent text-white text-sm flex-1 outline-none appearance-none">
                <option value="" className="bg-neutral-900">Select Role</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id} className="bg-neutral-900">
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-neutral-800/50 rounded-2xl border border-neutral-800 transition-all">
            <div className="flex flex-col">
              <span className="text-sm font-bold text-gray-200">Account Status</span>
              <span className="text-[10px] text-gray-400 font-medium">Toggle user access to the system</span>
            </div>
            <button onClick={() => handleChange('isActive', !form.isActive)} className="group transition-all active:scale-90">
              {form.isActive
                ? <ToggleRight className="w-8 h-8 text-green-500 transition-all" />
                : <ToggleLeft className="w-8 h-8 text-gray-500 transition-all" />
              }
            </button>
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-800/50 p-3 rounded-xl">
              <p className="text-red-400 text-xs font-bold flex items-center gap-2">
                <X className="w-3 h-3" /> {error}
              </p>
            </div>
          )}
        </div>

        <div className="p-6 bg-black/10 border-t border-neutral-800 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl text-gray-400 text-sm font-bold hover:bg-neutral-800 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold shadow-lg shadow-indigo-600/20 active:scale-95 transition-all"
          >
            {isEditMode ? 'Save Changes' : 'Create Account'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddUserModal;
